import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Trash2 } from "lucide-react";
import { EquipmentAutocomplete } from "./EquipmentAutocomplete";
import { InstanceTagsInput } from "./InstanceTagsInput";
import type { ProjectEquipmentAssignment } from "@/hooks/useProjectEquipment";
import type { EquipmentInstance } from "@/hooks/useEquipmentInstances";
import { formatEquipmentName } from "@/lib/equipment-utils";
import { useDraftField } from "@/hooks/useDraftField";

interface EquipmentAssignmentEditDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  assignment: ProjectEquipmentAssignment;
  instances?: EquipmentInstance[];
  onSave: (updates: {
    amount?: number;
    notes?: string;
    equipment_id?: string | null;
    temporary_equipment_id?: string | null;
  }) => void;
  onCreateTemporary: (name: string) => Promise<string>;
  onUpdateTemporary: (id: string, name: string) => void;
  onDelete: () => void;
  onAssignInstance: (instanceId: string) => void;
  onUnassignInstance: (assignmentInstanceId: string) => void;
}

export function EquipmentAssignmentEditDialog({
  open,
  onOpenChange,
  assignment,
  instances: instancesProp,
  onSave,
  onCreateTemporary,
  onUpdateTemporary,
  onDelete,
  onAssignInstance,
  onUnassignInstance,
}: EquipmentAssignmentEditDialogProps) {
  const [amount, setAmount] = useState(assignment.amount);
  const [selectedEquipment, setSelectedEquipment] = useState<{
    id: string;
    name: string;
    isTemporary: boolean;
  } | null>(null);
  const notesDraft = useDraftField<string, string | null>({
    value: assignment.notes,
    fromShared: (value) => value ?? "",
    toShared: (draft) => draft || null,
    onCommit: (notes) => {
      if (notes !== assignment.notes) {
        onSave({ notes });
      }
    },
  });
  const temporaryNameDraft = useDraftField<string>({
    value: assignment.temporary_equipment?.name ?? "",
    toShared: (draft) => draft.trim(),
    onCommit: (name) => {
      if (
        assignment.temporary_equipment_id &&
        name &&
        name !== assignment.temporary_equipment?.name
      ) {
        onUpdateTemporary(assignment.temporary_equipment_id, name);
      }
    },
  });

  const isSerialized = assignment.equipment?.is_serialized || false;
  const instances = instancesProp || [];

  const assignedInstanceIds = new Set(
    assignment.instance_assignments?.map((ia) => ia.instance_id) || [],
  );

  const availableInstances = instances.filter(
    (inst) => !assignedInstanceIds.has(inst.id) && inst.status === "active",
  );

  useEffect(() => {
    if (open) {
      setAmount(assignment.amount);
      setSelectedEquipment(
        assignment.equipment_id
          ? {
              id: assignment.equipment_id,
              name: assignment.equipment
                ? formatEquipmentName(
                    assignment.equipment.name,
                    assignment.equipment.manufacturer,
                  )
                : "",
              isTemporary: false,
            }
          : assignment.temporary_equipment_id
            ? {
                id: assignment.temporary_equipment_id,
                name: assignment.temporary_equipment?.name || "",
                isTemporary: true,
              }
            : null,
      );
    }
  }, [open, assignment]);

  const handleAmountChange = (value: number) => {
    setAmount(value);
    if (value !== assignment.amount) {
      onSave({ amount: value });
    }
  };

  const handleEquipmentChange = async (
    value: { id: string; name: string; isTemporary: boolean } | null,
  ) => {
    if (value) {
      setSelectedEquipment(value);
      if (value.isTemporary) {
        if (value.id !== assignment.temporary_equipment_id) {
          onSave({ temporary_equipment_id: value.id, equipment_id: null });
        }
      } else if (value.id !== assignment.equipment_id) {
        onSave({ equipment_id: value.id, temporary_equipment_id: null });
      }
    }
  };

  const handleCreateTemporaryInDialog = async (name: string) => {
    const tempId = await onCreateTemporary(name);
    setSelectedEquipment({ id: tempId, name, isTemporary: true });
    onSave({ temporary_equipment_id: tempId, equipment_id: null });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-md"
        onOpenAutoFocus={(e) => e.preventDefault()}
        onPointerDownOutside={(e) => {
          // Prevent dialog close when clicking on portaled autocomplete dropdown
          const target = e.target as HTMLElement;
          if (target.closest("[data-equipment-autocomplete]")) {
            e.preventDefault();
          }
        }}
      >
        <DialogHeader>
          <DialogTitle>Edit Equipment Assignment</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label>Equipment</Label>
            {assignment.temporary_equipment_id ? (
              <Input
                value={temporaryNameDraft.draft}
                onFocus={temporaryNameDraft.start}
                onChange={(e) => temporaryNameDraft.setDraft(e.target.value)}
                onBlur={() => void temporaryNameDraft.commit()}
                placeholder="Temporary equipment name..."
              />
            ) : (
              <EquipmentAutocomplete
                value={selectedEquipment}
                onChange={handleEquipmentChange}
                onCreateTemporary={handleCreateTemporaryInDialog}
              />
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="amount">Amount</Label>
            <Input
              id="amount"
              type="number"
              min={1}
              value={amount}
              onChange={(e) =>
                handleAmountChange(parseInt(e.target.value) || 1)
              }
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="notes">Notes</Label>
            <Textarea
              id="notes"
              value={notesDraft.draft}
              onFocus={notesDraft.start}
              onChange={(e) => notesDraft.setDraft(e.target.value)}
              onBlur={() => void notesDraft.commit()}
              placeholder="Add notes about this equipment..."
              rows={3}
            />
          </div>

          {isSerialized && (
            <div className="space-y-2">
              <Label>Assigned Instances</Label>
              <InstanceTagsInput
                assignedInstances={assignment.instance_assignments || []}
                availableInstances={availableInstances}
                onAssign={onAssignInstance}
                onUnassign={onUnassignInstance}
                maxInstances={amount}
              />
            </div>
          )}

          <Button
            type="button"
            variant="ghost"
            className="w-full justify-start text-destructive hover:text-destructive"
            onClick={() => {
              onDelete();
              onOpenChange(false);
            }}
          >
            <Trash2 className="h-4 w-4 mr-2" />
            Delete assignment
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
